import { useMemo } from 'react';
import {
  collapseSingleChildLeaves,
  NestedOptions,
  buildDescendantMap,
  sortNestedOptionsTree,
  extractValueLabels,
} from '../TreeSelect.helpers';
import { useNestedKeyComparator } from '../useNestedKeyComparator';
import { SelectItem } from '@/types/ui';
import type { NestedKeySort } from '../TreeSelect.types';

/** Walks the tree by path segments in O(depth). Returns the matching node wrapped in `{ [label]: node }`, or undefined. */
function searchOptionByPath(path: string, nestedObject: NestedOptions): NestedOptions | undefined {
  if (!path) return undefined;
  const parts = path.split('__');
  let currentLevel = nestedObject;

  for (let i = 0; i < parts.length - 1; i++) {
    const node = currentLevel[parts[i]];
    if (!node) return undefined;
    currentLevel = node.subOptions;
  }

  const lastPart = parts[parts.length - 1];
  const target = currentLevel[lastPart];
  return target ? { [lastPart]: target } : undefined;
}

// Build-phase mutation: called inside useMemo before the tree is returned,
// so the object is still local and not yet exposed to React's render cycle.
function updateParentStates(nestedObject: NestedOptions) {
  const updateParentCheckStates = (currentNestedOption: NestedOptions) => {
    Object.values(currentNestedOption).forEach(option => {
      const subOptions = option.subOptions;
      const subKeys = Object.keys(subOptions);
      if (subKeys.length === 0) return;
      updateParentCheckStates(subOptions);
      let checkedChildren = 0;
      let indeterminateChildren = 0;
      subKeys.forEach(key => {
        const nestedOption = subOptions[key];
        if (nestedOption.checked === true) checkedChildren += 1;
        if (nestedOption.checked === 'indeterminate') indeterminateChildren += 1;
      });
      if (checkedChildren === subKeys.length) option.checked = true;
      else if (checkedChildren > 0 || indeterminateChildren > 0) option.checked = 'indeterminate';
      else option.checked = false;
    });
  };
  updateParentCheckStates(nestedObject);
}

type UseOldTreeSelectOptionsParams<T extends string> = {
  options: SelectItem<T>[];
  value: T[];
  collapseSingleChild?: boolean;
  selectionMode: 'standard' | 'indication';
  nestedKeySort: NestedKeySort;
};

export function useOldTreeSelectOptions<T extends string>({
  options,
  value,
  collapseSingleChild,
  selectionMode,
  nestedKeySort,
}: UseOldTreeSelectOptionsParams<T>) {
  const fixedOptions = useMemo(() => {
    const purgedOptions = options.filter(opt => !!opt.value);
    return collapseSingleChild ? collapseSingleChildLeaves(purgedOptions) : purgedOptions;
  }, [collapseSingleChild, options]);

  const descendantMap = useMemo(() => buildDescendantMap(fixedOptions, selectionMode), [fixedOptions, selectionMode]);

  // Unlike useTreeSelectOptions (which builds a structural-only tree independent of `value`),
  // this tree must include checked state because the OldTreeSelect handler mutates `.checked`
  // in-place and then calls `getAllCheckedPaths` to derive the new value. This means the tree
  // is rebuilt + re-sorted on every checkbox click — an intentional trade-off to preserve the
  // old variant's collapsing selection behavior.
  const nestedOptions = useMemo((): NestedOptions => {
    const nestedObject: NestedOptions = {};
    const selectedSet = new Set<string>(value);

    for (const option of fixedOptions) {
      const parts = option.value.split('__');
      let currentLevel = nestedObject;
      let currentPath = '';
      let parentIsChecked = false;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        currentPath = i === 0 ? part : `${currentPath}__${part}`;

        if (!currentLevel[part]) {
          const isSelected = selectedSet.has(currentPath);
          currentLevel[part] = {
            path: currentPath,
            subOptions: {},
            checked: selectionMode === 'indication' ? isSelected : parentIsChecked || isSelected,
          };
        }

        // Propagate checked state downward for the next level
        parentIsChecked = currentLevel[part].checked === true;
        currentLevel = currentLevel[part].subOptions;
      }
    }

    // In standard mode, propagate checked state upward (indeterminate / fully checked)
    if (selectionMode === 'standard') {
      updateParentStates(nestedObject);
    }
    return nestedObject;
  }, [fixedOptions, value, selectionMode]);

  const compareNestedKeys = useNestedKeyComparator(nestedKeySort);

  const sortedNestedOptions = useMemo(
    () => sortNestedOptionsTree(nestedOptions, compareNestedKeys),
    [nestedOptions, compareNestedKeys],
  );

  const valueLabelList = useMemo(() => extractValueLabels(value), [value]);

  return {
    fixedOptions,
    descendantMap,
    nestedOptions,
    sortedNestedOptions,
    valueLabelList,
    searchOptionByPath,
  };
}
